import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowUpRight, BellOff, UserCog } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function InsightPanel({ open, onOpenChange, analysis, deal, onSnooze, onRequestReview }) {
  if (!analysis) return null;
  const risk = analysis.risk_level;

  const handleSnooze = async () => {
    try {
      await api.post("/alert-logs", {
        deal_id: deal?.id,
        analysis_id: analysis.id,
        action: "snoozed",
      });
      toast.success("Alert snoozed for 24 hours");
      onSnooze?.();
      onOpenChange(false);
    } catch {
      toast.error("Could not snooze");
    }
  };

  const handleReview = async () => {
    try {
      await api.post("/review-requests", {
        deal_id: deal?.id,
        analysis_id: analysis.id,
        note: analysis.narrative,
      });
      toast.success("Review requested", { description: "Your manager has been notified." });
      onRequestReview?.();
      onOpenChange(false);
    } catch {
      toast.error("Could not send review request");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[460px] p-0 rounded-none border-l border-gray-200 flex flex-col"
        data-testid="insight-panel"
      >
        <SheetHeader className="p-6 border-b border-gray-200 space-y-0">
          <div className="flex items-center justify-between mb-4">
            <span className={`risk-pill ${risk.toLowerCase()}`} data-testid="panel-risk-pill">
              <AlertTriangle className="w-3 h-3" /> {risk} RISK
            </span>
            <span className="overline text-gray-400">iSale Insight</span>
          </div>
          <SheetTitle className="font-heading text-xl tracking-tight text-left">
            {deal?.deal_name || "Deal Insight"}
          </SheetTitle>
          <div className="text-xs text-gray-500 mt-1">{deal?.company}</div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 border-b border-gray-200">
            <div className="p-6 border-r border-gray-200">
              <div className="overline text-gray-400 mb-2">Reality Score</div>
              <div className="metric-value text-5xl tracking-tighter" data-testid="reality-score">
                {analysis.reality_score?.toFixed(1)}
                <span className="text-xl text-gray-300">/10</span>
              </div>
            </div>
            <div className="p-6">
              <div className="overline text-gray-400 mb-2">Contradiction Gap</div>
              <div
                className={`metric-value text-5xl tracking-tighter ${
                  analysis.contradiction_gap >= 1.8 ? "text-[#DC2626]" : "text-gray-900"
                }`}
                data-testid="contradiction-gap"
              >
                {analysis.contradiction_gap >= 0 ? "+" : ""}
                {analysis.contradiction_gap?.toFixed(1)}
              </div>
            </div>
          </div>

          <div className="p-6 border-b border-gray-200">
            <div className="overline text-gray-400 mb-3">Narrative</div>
            <p className="text-base leading-relaxed text-gray-900" data-testid="narrative-text">
              {analysis.narrative}
            </p>
          </div>

          <div className="p-6">
            <div className="overline text-gray-400 mb-4">
              Top Risk Signals ({analysis.risk_signals?.length || 0})
            </div>
            <div className="space-y-3" data-testid="risk-signals-list">
              {(analysis.risk_signals || []).map((s, i) => (
                <div
                  key={i}
                  className={`border-l-2 pl-4 py-2 ${
                    s.severity === "high"
                      ? "border-[#DC2626]"
                      : s.severity === "medium"
                      ? "border-[#EA580C]"
                      : "border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`overline ${
                      s.severity === "high" ? "text-[#DC2626]" :
                      s.severity === "medium" ? "text-[#EA580C]" : "text-gray-400"
                    }`}>
                      {s.severity}
                    </span>
                    <span className="text-sm font-semibold">{s.label}</span>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">{s.detail}</div>
                </div>
              ))}
              {(!analysis.risk_signals || analysis.risk_signals.length === 0) && (
                <div className="text-sm text-gray-400">No risk signals detected.</div>
              )}
            </div>
          </div>

          <div className="px-6 pb-6">
            <div className="overline text-gray-400 mb-3">Telemetry</div>
            <div className="text-xs font-mono space-y-1 text-gray-500">
              <div className="flex justify-between">
                <span>intuition_score</span>
                <span>{analysis.intuition_score?.toFixed(2) ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>missing_data_ratio</span>
                <span>{analysis.missing_data_ratio?.toFixed(2) ?? "—"}</span>
              </div>
              <div className="flex justify-between">
                <span>confidence</span>
                <span>{analysis.confidence?.toFixed(2) ?? "—"}</span>
              </div>
            </div>
          </div>

          {analysis.fft?.enabled && (
            <div className="px-6 pb-6" data-testid="fft-block">
              <div className="flex items-center justify-between mb-3">
                <span className="overline text-gray-400">FFT Anomaly Signal</span>
                {analysis.fft.alert_level && (
                  <span className={`risk-pill ${analysis.fft.alert_level.toLowerCase()}`} data-testid="fft-alert-level">
                    {analysis.fft.alert_level}
                  </span>
                )}
              </div>
              {analysis.fft.skipped_reason ? (
                <div className="text-xs text-gray-400 italic">
                  Skipped: {analysis.fft.skipped_reason.replace(/_/g, " ")} ({analysis.fft.event_count ?? 0} events)
                </div>
              ) : (
                <>
                  <div className="flex items-end justify-between mb-2">
                    <div>
                      <div className="metric-value text-3xl leading-none" data-testid="fft-anomaly-score">
                        {(analysis.fft.anomaly_score ?? 0).toFixed(2)}
                      </div>
                      <div className="overline text-gray-400 mt-1">anomaly / 1.00</div>
                    </div>
                    <div className="text-right text-xs font-mono text-gray-500 space-y-0.5">
                      <div>segment: {analysis.fft.deal_type}</div>
                      <div>events: {analysis.fft.event_count}</div>
                      <div>{analysis.fft.latency_ms?.toFixed(1)} ms</div>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-sm overflow-hidden mb-3">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${Math.min(100, (analysis.fft.anomaly_score ?? 0) * 100)}%`,
                        backgroundColor:
                          analysis.fft.alert_level === "HIGH" ? "#DC2626" :
                          analysis.fft.alert_level === "MEDIUM" ? "#EA580C" : "#9CA3AF",
                      }}
                    />
                  </div>
                  {analysis.fft.spectrum && (
                    <div className="flex items-end gap-[1px] h-10" data-testid="fft-spectrum-chart">
                      {analysis.fft.spectrum.slice(0, 40).map((v, i) => {
                        const max = Math.max(...analysis.fft.spectrum.slice(0, 40));
                        const h = max > 0 ? (v / max) * 100 : 0;
                        return (
                          <div
                            key={i}
                            className="flex-1"
                            style={{
                              height: `${h}%`,
                              backgroundColor:
                                analysis.fft.alert_level === "HIGH" ? "#DC2626" :
                                analysis.fft.alert_level === "MEDIUM" ? "#EA580C" : "#111827",
                              opacity: 0.6 + (h / 250),
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                  {!analysis.fft.reference_available && (
                    <div className="text-xs text-gray-400 italic mt-2">
                      No baseline yet — score will stabilize after profile generation.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={handleSnooze}
            data-testid="panel-snooze-btn"
            className="rounded-sm h-10 gap-2"
          >
            <BellOff className="w-3.5 h-3.5" />
            Snooze 24h
          </Button>
          <Button
            onClick={handleReview}
            data-testid="panel-request-review-btn"
            className="rounded-sm h-10 gap-2 bg-black hover:bg-gray-800"
          >
            <UserCog className="w-3.5 h-3.5" />
            Request Review
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
